import {
  DeviceCheckNotes,
  FieldProse,
  NumberedSection,
  obj,
  PanelCard,
  Placeholder,
} from './DossierPrimitives';

/**
 * EU MDR Annex III PMS Plan renderer — §1–§5 covering data collection,
 * data assessment, protocols, CAPA, and PMCF.
 *
 * Schema detected by the parent renderer via presence of `data_collection`
 * in the dossier dict (unique to AnnexIIIPmsPlan; no US eSTAR/eCTD schema
 * nor the EU TD schema carries this top-level key).
 *
 * Key features:
 *   - Cover header block (device class, Basic UDI-DI, company, EC REP, NB)
 *   - §1 Data Collection (6 sub-fields)
 *   - §2 Data Assessment (3 sub-fields)
 *   - §3 Communication Protocols (2 sub-fields)
 *   - §4 CAPA (2 sub-fields)
 *   - §5 PMCF Plan (1 sub-field)
 *
 * Placeholder discipline: values equal to `__PLACEHOLDER__` or starting
 * with `__PLACEHOLDER__:` are surfaced by the shared Placeholder primitive
 * as amber italic "to complete" chips (identical to EuTdRenderer).
 */

// ── Types ────────────────────────────────────────────────────────────────────

interface SubmissionHeader {
  product_name?: string | null;
  company?: string | null;
  ec_rep?: string | null;
  notified_body?: string | null;
  prrc?: string | null;
  srn?: string | null;
  date_of_issue?: string | null;
}

// ── Main component ───────────────────────────────────────────────────────────

export function EuPmsRenderer({
  dossier,
  submissionHeader,
  hideCover = false,
}: {
  dossier: Record<string, unknown>;
  submissionHeader?: SubmissionHeader;
  /** Grouped EU view: the shared header card supplies the identity, so the
   *  per-annex cover block is suppressed. Default false → standalone output is
   *  byte-identical. */
  hideCover?: boolean;
}) {
  return (
    <div className="space-y-4">
      {!hideCover && <PmsCoverBlock dossier={dossier} submissionHeader={submissionHeader} />}
      <DeviceCheckNotes dossier={dossier} />
      <Section1DataCollection dossier={dossier} />
      <Section2DataAssessment dossier={dossier} />
      <Section3Protocols dossier={dossier} />
      <Section4Capa dossier={dossier} />
      <Section5Pmcf dossier={dossier} />
    </div>
  );
}

// ── Cover block ──────────────────────────────────────────────────────────────

function PmsCoverBlock({
  dossier,
  submissionHeader,
}: {
  dossier: Record<string, unknown>;
  submissionHeader?: SubmissionHeader;
}) {
  const deviceClass = dossier['device_class'];
  const basicUdi = dossier['basic_udi_di'];

  const company = submissionHeader?.company;
  const ecRep = submissionHeader?.ec_rep;
  const notifiedBody = submissionHeader?.notified_body;
  const dateOfIssue = submissionHeader?.date_of_issue;

  return (
    <PanelCard>
      <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
        EU MDR Annex III PMS Plan
      </div>
      <p className="text-[11px] text-muted m-0 mb-4">
        Regulation (EU) 2017/745 — Annex III (Post-Market Surveillance)
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CoverCell
          label="Device trade name"
          value={submissionHeader?.product_name}
          fallbackHint="Device trade name"
        />
        <CoverCell label="Device class (EU MDR)" value={deviceClass} />
        <CoverCell
          label="Basic UDI-DI"
          value={basicUdi}
          fallbackHint="Assigned upon registration in EUDAMED"
        />
        <CoverCell
          label="Manufacturer / legal manufacturer"
          value={company}
          fallbackHint="Manufacturer name and address"
        />
        <CoverCell
          label="EU Authorised Representative (EC REP)"
          value={ecRep}
          fallbackHint="EC REP name and address"
        />
        <CoverCell
          label="Notified Body (NB)"
          value={notifiedBody}
          fallbackHint="NB name and number (if applicable)"
        />
        <CoverCell
          label="Date of issue"
          value={dateOfIssue}
          fallbackHint="Date of issue (YYYY-MM-DD)"
        />
      </div>
    </PanelCard>
  );
}

function CoverCell({
  label,
  value,
  fallbackHint,
}: {
  label: string;
  value: unknown;
  fallbackHint?: string;
}) {
  const v = value == null ? '' : String(value).trim();
  const isPlaceholder = v === '__PLACEHOLDER__' || v.startsWith('__PLACEHOLDER__:');
  const hint = isPlaceholder
    ? v.startsWith('__PLACEHOLDER__:')
      ? v.slice('__PLACEHOLDER__:'.length).trim() || fallbackHint || label
      : fallbackHint || label
    : null;

  return (
    <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
      <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
        {label}
      </div>
      <div className="text-[13px] text-navy">
        {isPlaceholder ? (
          <Placeholder hint={hint} />
        ) : v ? (
          v
        ) : (
          <Placeholder hint={fallbackHint || label} />
        )}
      </div>
    </div>
  );
}

// ── §1 Data Collection ────────────────────────────────────────────────────────

const DATA_COLLECTION_FIELDS: [string, string][] = [
  ['serious_incidents_and_fscas', 'Serious incidents and FSCAs (MDR Art. 87–89)'],
  ['non_serious_incidents_and_side_effects', 'Non-serious incidents and side effects'],
  ['trend_data', 'Trend data (Art 88)'],
  ['specialist_literature_databases_registers', 'Specialist literature, databases, and registers'],
  ['feedback_and_complaints', 'Feedback and complaints'],
  ['public_info_on_similar_devices', 'Public information on similar devices'],
];

function Section1DataCollection({ dossier }: { dossier: Record<string, unknown> }) {
  const dc = obj(dossier, 'data_collection');
  return (
    <NumberedSection prefix="Section" num={1} title="Data Collection">
      {DATA_COLLECTION_FIELDS.map(([key, label]) => {
        const val = dc[key];
        if (val == null) return null;
        return (
          <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
        );
      })}
    </NumberedSection>
  );
}

// ── §2 Data Assessment ────────────────────────────────────────────────────────

const DATA_ASSESSMENT_FIELDS: [string, string][] = [
  ['methods_to_assess_collected_data', 'Methods to assess collected data'],
  [
    'indicators_and_thresholds_for_benefit_risk_reassessment',
    'Indicators and thresholds for benefit-risk reassessment',
  ],
  [
    'methods_to_investigate_complaints_and_market_experience',
    'Methods to investigate complaints and market experience',
  ],
];

function Section2DataAssessment({ dossier }: { dossier: Record<string, unknown> }) {
  const da = obj(dossier, 'data_assessment');
  const hasContent = DATA_ASSESSMENT_FIELDS.some(([k]) => da[k] != null);
  return (
    <NumberedSection prefix="Section" num={2} title="Data Assessment">
      {hasContent ? (
        DATA_ASSESSMENT_FIELDS.map(([key, label]) => {
          const val = da[key];
          if (val == null) return null;
          return (
            <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
          );
        })
      ) : (
        <FieldProse
          label="Data assessment:"
          body={null}
          fallbackHint="Data assessment methods to be completed"
        />
      )}
    </NumberedSection>
  );
}

// ── §3 Protocols ──────────────────────────────────────────────────────────────

const PROTOCOL_FIELDS: [string, string][] = [
  ['trend_reporting_protocol', 'Trend reporting protocol (MDR Art. 88)'],
  ['communication_protocols', 'Communication protocols (Art 84(1)(c))'],
];

function Section3Protocols({ dossier }: { dossier: Record<string, unknown> }) {
  const pr = obj(dossier, 'protocols');
  const hasContent = PROTOCOL_FIELDS.some(([k]) => pr[k] != null);
  return (
    <NumberedSection prefix="Section" num={3} title="Protocols">
      {hasContent ? (
        PROTOCOL_FIELDS.map(([key, label]) => {
          const val = pr[key];
          if (val == null) return null;
          return (
            <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
          );
        })
      ) : (
        <FieldProse
          label="Protocols:"
          body={null}
          fallbackHint="Trend reporting and communication protocols to be completed"
        />
      )}
    </NumberedSection>
  );
}

// ── §4 CAPA ───────────────────────────────────────────────────────────────────

const CAPA_FIELDS: [string, string][] = [
  [
    'procedures_to_identify_and_initiate_capa',
    'Procedures to identify and initiate CAPA',
  ],
  ['tools_to_trace_devices_needing_capa', 'Tools to trace devices needing CAPA'],
];

function Section4Capa({ dossier }: { dossier: Record<string, unknown> }) {
  const capa = obj(dossier, 'capa');
  const hasContent = CAPA_FIELDS.some(([k]) => capa[k] != null);
  return (
    <NumberedSection
      prefix="Section"
      num={4}
      title="Corrective and Preventive Actions (CAPA)"
    >
      {hasContent ? (
        CAPA_FIELDS.map(([key, label]) => {
          const val = capa[key];
          if (val == null) return null;
          return (
            <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
          );
        })
      ) : (
        <FieldProse
          label="CAPA:"
          body={null}
          fallbackHint="CAPA procedures and tracing tools to be completed"
        />
      )}
    </NumberedSection>
  );
}

// ── §5 PMCF ───────────────────────────────────────────────────────────────────

function Section5Pmcf({ dossier }: { dossier: Record<string, unknown> }) {
  const pmcf = obj(dossier, 'pmcf');
  const val = pmcf['pmcf_plan_or_justification'];
  return (
    <NumberedSection
      prefix="Section"
      num={5}
      title="Post-Market Clinical Follow-up (PMCF)"
    >
      <FieldProse
        label="PMCF plan or justification for non-applicability:"
        body={val ?? null}
        fallbackHint="PMCF plan or justification to be completed per MDCG 2020-7"
      />
    </NumberedSection>
  );
}
